import React, { useState, useCallback } from 'react'
import { Button, Card, LocationInput, LocationError, ConditionSelector, PlanConditions, LoadingState, ErrorState, ShareModal, PlanCarousel } from './components'
import { PlanCardDemo } from './components/PlanCardDemo'
import ImageTest from './components/ImageTest'
import { Location, Plan, PlanRequest, PlanResponse } from './types/core'
import { filterPlansWithImages } from './utils/spot-image-validator'
import './styles/carousel.css'

// API endpoint configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://flaato-api.flaato-app.workers.dev/api'

function App() {
  const [currentLocation, setCurrentLocation] = useState<Location | null>(null);
  const [locationError, setLocationError] = useState<LocationError | null>(null);
  const [planConditions, setPlanConditions] = useState<PlanConditions | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedPlanForShare, setSelectedPlanForShare] = useState<Plan | null>(null);

  const handleLocationChange = useCallback((location: Location | null) => {
    setCurrentLocation(location);
    setLocationError(null);
    // Clear previous plans when location changes
    setPlans([]);
    setApiError(null);
  }, []);

  const handleLocationError = useCallback((error: LocationError) => {
    setLocationError(error);
    setCurrentLocation(null);
  }, []);

  const handleConditionsChange = useCallback((conditions: PlanConditions) => {
    setPlanConditions(conditions);
    // Clear previous plans when conditions change
    setPlans([]);
    setApiError(null);

    // 条件設定後に自動的にプラン生成を開始
    setTimeout(() => {
      if (currentLocation) {
        generatePlansWithConditions(conditions);
      }
    }, 500); // 少し遅延を入れてUIの更新を待つ
  }, [currentLocation]);

  // Initialize session if needed
  const initializeSession = useCallback(async () => {
    try {
      console.log('Initializing session...');
      const response = await fetch(`${API_BASE_URL}/session`, {
        method: 'POST',
        credentials: 'include'
      });
      console.log('Session initialization response:', response.status);
      console.log('Session cookies:', response.headers.get('Set-Cookie'));
    } catch (error) {
      console.warn('Session initialization failed:', error);
    }
  }, []);

  // API call function to generate plans with specific conditions
  const generatePlansWithConditions = useCallback(async (conditions: PlanConditions) => {
    if (!currentLocation) {
      return;
    }

    setIsLoading(true);
    setLoadingProgress(0);
    setApiError(null);
    setPlans([]);

    try {
      // Initialize session first
      await initializeSession();

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      // Prepare API request
      const planRequest: PlanRequest = {
        lat: currentLocation.lat,
        lng: currentLocation.lng,
        duration: conditions.duration,
        genres: conditions.genres,
        people: conditions.people,
        rainBias: conditions.rainBias
      };

      // Build query parameters for GET request
      const queryParams = new URLSearchParams({
        lat: planRequest.lat.toString(),
        lng: planRequest.lng.toString(),
        duration: planRequest.duration.toString(),
        people: planRequest.people.toString(),
        rainBias: planRequest.rainBias.toString()
      });

      // Add genres as multiple parameters
      planRequest.genres.forEach(genre => {
        queryParams.append('genres', genre);
      });

      // Make API call
      const requestUrl = `${API_BASE_URL}/plan?${queryParams.toString()}`;
      console.log('Making API request to:', requestUrl);

      const response = await fetch(requestUrl, {
        method: 'GET',
        credentials: 'include' // Include session cookie
      });

      clearInterval(progressInterval);
      setLoadingProgress(100);

      console.log('API response status:', response.status);
      console.log('API response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);

        if (response.status === 429) {
          throw new Error('アクセス制限に達しました。しばらく時間をおいてから再試行してください。');
        } else if (response.status === 403) {
          throw new Error('セッションエラーが発生しました。ページを再読み込みしてください。');
        } else if (response.status === 404) {
          throw new Error('APIエンドポイントが見つかりません。設定を確認してください。');
        } else if (response.status >= 500) {
          throw new Error('サーバーエラーが発生しました。しばらく待ってから再試行してください。');
        } else {
          throw new Error(`プランの生成に失敗しました。(${response.status})`);
        }
      }

      const planResponse: PlanResponse = await response.json();

      if (planResponse.plans && planResponse.plans.length > 0) {
        // 画像があるスポットのみを含むプランにフィルタリング
        const filteredPlans = filterPlansWithImages(planResponse.plans);

        if (filteredPlans.length > 0) {
          setPlans(filteredPlans);
          console.log(`プランをフィルタリング: ${planResponse.plans.length} → ${filteredPlans.length}`);
        } else {
          setApiError('表示可能なスポットが見つかりませんでした。条件を変更して再試行してください。');
        }
      } else {
        setApiError('条件に合うプランが見つかりませんでした。条件を変更して再試行してください。');
      }

    } catch (error) {
      console.error('Plan generation failed:', error);

      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          setApiError('ネットワークエラーが発生しました。インターネット接続を確認してください。');
        } else {
          setApiError(error.message);
        }
      } else {
        setApiError('予期しないエラーが発生しました。');
      }
    } finally {
      setIsLoading(false);
      setLoadingProgress(0);
    }
  }, [currentLocation, initializeSession]);

  // API call function to generate plans
  const generatePlans = useCallback(async () => {
    if (!currentLocation || !planConditions) {
      return;
    }

    return generatePlansWithConditions(planConditions);
  }, [currentLocation, planConditions, generatePlansWithConditions]);

  // Handle plan sharing
  const handlePlanShare = useCallback((plan: Plan) => {
    setSelectedPlanForShare(plan);
    setShareModalOpen(true);
  }, []);

  // Handle loading cancellation
  const handleCancelLoading = useCallback(() => {
    setIsLoading(false);
    setLoadingProgress(0);
  }, []);

  // Handle error retry
  const handleRetryError = useCallback(() => {
    setApiError(null);
    generatePlans();
  }, [generatePlans]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Skip link for keyboard navigation */}
      <a
        href="#main-content"
        className="skip-link"
        aria-label="メインコンテンツにスキップ"
      >
        メインコンテンツにスキップ
      </a>

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                2時間マイクロ外出プランナー
              </h1>
            </div>

            {/* Navigation placeholder */}
            <nav role="navigation" aria-label="メインナビゲーション">
              <div className="flex space-x-4">
                {/* Navigation items will be added in future tasks */}
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main
        id="main-content"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        role="main"
      >
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            今から短時間で楽しめる外出プランを提案
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            現在地と条件を入力するだけで、2〜3ステップの最適な外出プランを即座に提案します。
            東京エリア対応、30分から最大3時間まで。
          </p>

          {/* Location Input */}
          <Card className="max-w-2xl mx-auto mb-8">
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  ステップ1: 現在地を取得
                </h3>
                <p className="text-gray-600">
                  現在地を取得してプラン検索を開始しましょう
                </p>
              </div>

              <LocationInput
                onLocationChange={handleLocationChange}
                onError={handleLocationError}
              />

              {currentLocation && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
                  <h4 className="font-medium text-green-900 mb-3">✓ 位置情報を取得しました</h4>
                  <div className="flex items-center justify-center p-8 bg-gradient-to-br from-green-100 to-green-200 rounded-md">
                    <div className="text-center">
                      <div className="text-green-600 mb-2">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <p className="text-sm font-medium text-green-800 mb-1">現在地</p>
                      <p className="text-xs text-green-600">
                        緯度: {currentLocation.lat.toFixed(6)}
                      </p>
                      <p className="text-xs text-green-600">
                        経度: {currentLocation.lng.toFixed(6)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Condition Selector */}
          {currentLocation && !isLoading && plans.length === 0 && !apiError && (
            <Card className="max-w-2xl mx-auto">
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    外出条件を設定してプランを生成
                  </h3>
                  <p className="text-gray-600">
                    時間、ジャンル、人数を選択してプランを生成します
                  </p>
                </div>

                <ConditionSelector
                  onConditionsChange={handleConditionsChange}
                />

                {planConditions && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                    <h4 className="font-medium text-blue-900 mb-2">条件設定完了 - プラン生成中...</h4>
                    <div className="text-sm text-blue-800 text-center">
                      {planConditions.duration}分 • {planConditions.genres.join(', ')} • {planConditions.people === 4 ? '3-4人' : `${planConditions.people}人`}
                      {planConditions.rainBias && ' • 雨寄せON'}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="fixed inset-0 bg-white bg-opacity-80 backdrop-blur-sm flex items-center justify-center z-50">
              <Card className="max-w-md mx-4 shadow-2xl">
                <LoadingState
                  message="最適なプランを生成中..."
                  progress={loadingProgress}
                  showProgress={true}
                  showCancel={true}
                  onCancel={handleCancelLoading}
                />
              </Card>
            </div>
          )}

          {/* Error State */}
          {apiError && !isLoading && (
            <Card className="max-w-2xl mx-auto">
              <ErrorState
                errorType="api_error"
                message={apiError}
                showRetry={true}
                onRetry={handleRetryError}
                showAlternative={true}
                onAlternative={() => {
                  setApiError(null);
                  setPlans([]);
                }}
                alternativeText="条件を変更"
              />
            </Card>
          )}

          {/* Location Error State */}
          {locationError && (
            <Card className="max-w-2xl mx-auto">
              <ErrorState
                errorType={locationError.code === 'PERMISSION_DENIED' ? 'location_denied' : 'location_unavailable'}
                message={locationError.message}
                showRetry={true}
                onRetry={() => {
                  setLocationError(null);
                  // Trigger location input to retry
                }}
              />
            </Card>
          )}

          {/* Generated Plans */}
          {plans.length > 0 && !isLoading && (
            <div className="space-y-8">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  おすすめプラン
                </h3>
                <p className="text-gray-600">
                  {plans.length}件のプランが見つかりました
                </p>
              </div>

              <div className="max-w-4xl mx-auto">
                <PlanCarousel
                  plans={plans}
                  startLocation={currentLocation || undefined}
                  onShare={handlePlanShare}
                  className="w-full"
                />
              </div>

              {/* Action buttons for new search */}
              <div className="text-center space-y-4">
                <div className="flex justify-center gap-4">
                  <Button
                    onClick={() => {
                      setPlans([]);
                      setApiError(null);
                      setPlanConditions(null);
                    }}
                    variant="outline"
                  >
                    条件を変更
                  </Button>
                  <Button
                    onClick={generatePlans}
                    variant="secondary"
                    disabled={isLoading}
                  >
                    別のプランを生成
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Image Test Section - For debugging */}
        {process.env.NODE_ENV === 'development' && (
          <section id="image-test" className="mt-16 pt-8 border-t border-gray-200">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                画像テスト（開発用）
              </h3>
            </div>
            <ImageTest />
          </section>
        )}

        {/* Demo Section - Only show when no real plans are available */}
        {plans.length === 0 && !isLoading && !apiError && (
          <section id="plan-demo" className="mt-16 pt-8 border-t border-gray-200">
            <PlanCardDemo />
          </section>
        )}
      </main>

      {/* Share Modal */}
      {shareModalOpen && selectedPlanForShare && (
        <ShareModal
          plan={selectedPlanForShare}
          startLocation={currentLocation!}
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setSelectedPlanForShare(null);
          }}
        />
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-16" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

            {/* API Credits Section - Required by Hot Pepper API terms */}
            <div className="text-center md:text-left">
              <h4 className="font-semibold text-gray-900 mb-3">データ提供</h4>
              <div className="space-y-2 text-sm text-gray-600">
                {/* Hot Pepper Credit - Must be prominently displayed */}
                <div className="p-2 bg-orange-50 border border-orange-200 rounded">
                  <a
                    href="http://webservice.recruit.co.jp/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-700 hover:text-orange-800 font-medium"
                    aria-label="ホットペッパー Webサービス（外部リンク）"
                  >
                    Powered by ホットペッパー Webサービス
                  </a>
                </div>

                {/* Other API Credits */}
                <div className="space-y-1">
                  <p>
                    <a
                      href="https://developer.yahoo.co.jp/webapi/map/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700"
                      aria-label="Yahoo!ローカルサーチAPI（外部リンク）"
                    >
                      Yahoo!ローカルサーチ API
                    </a>
                  </p>
                  <p>
                    <a
                      href="https://www.openstreetmap.org/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-green-600 hover:text-green-700"
                      aria-label="OpenStreetMap（外部リンク）"
                    >
                      OpenStreetMap
                    </a>
                    {' '}© OpenStreetMap contributors
                  </p>
                  <p>
                    <a
                      href="https://www.doorkeeper.jp/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:text-purple-700"
                      aria-label="Doorkeeper（外部リンク）"
                    >
                      Doorkeeper API
                    </a>
                  </p>
                  <p>
                    <a
                      href="https://open-meteo.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-700"
                      aria-label="Open-Meteo（外部リンク）"
                    >
                      Open-Meteo API
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Accessibility Information */}
            <div className="text-center md:text-left">
              <h4 className="font-semibold text-gray-900 mb-3">アクセシビリティ</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <p>このサイトはWCAG 2.1 AA基準に準拠しています</p>
                <div className="space-y-1">
                  <p>• キーボードナビゲーション対応</p>
                  <p>• スクリーンリーダー対応</p>
                  <p>• 高コントラスト表示</p>
                  <p>• レスポンシブデザイン</p>
                </div>
                <div className="mt-3">
                  <p className="text-xs">
                    アクセシビリティに関するご意見・ご要望は
                    <br />
                    <a
                      href="mailto:accessibility@example.com"
                      className="text-blue-600 hover:text-blue-700"
                      aria-label="アクセシビリティに関するお問い合わせメール"
                    >
                      accessibility@example.com
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Legal and Usage Information */}
            <div className="text-center md:text-left">
              <h4 className="font-semibold text-gray-900 mb-3">利用規約・免責事項</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <p>• 営業時間・料金情報は変更される場合があります</p>
                <p>• 実際の訪問前に各店舗にご確認ください</p>
                <p>• 天気予報は参考情報です</p>
                <p>• 徒歩時間は目安です</p>

                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs">
                    このサービスは無料で提供されており、
                    <br />
                    サーバー負荷軽減のため利用制限があります
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright and Version */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
              <p className="text-sm text-gray-500">
                © 2024 2時間マイクロ外出プランナー
              </p>

              <div className="flex items-center space-x-4 text-xs text-gray-400">
                <span>Version 1.0.0</span>
                <span>•</span>
                <span>東京エリア対応</span>
                <span>•</span>
                <span>
                  <a
                    href="#main-content"
                    className="text-blue-600 hover:text-blue-700"
                    aria-label="ページトップに戻る"
                  >
                    ↑ トップに戻る
                  </a>
                </span>
              </div>
            </div>
          </div>

          {/* Screen reader only additional information */}
          <div className="sr-only">
            <h4>追加のアクセシビリティ情報</h4>
            <p>
              このWebアプリケーションは、視覚障害者、聴覚障害者、運動障害者の方々にも
              ご利用いただけるよう設計されています。
              ご不明な点やご要望がございましたら、お気軽にお問い合わせください。
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App